// My agent › Hire as a DE (M7-05; 05 §Personal crews "hire as DE", 07 §Agent keys per DE): a
// manager turns their personal agent into a digital employee. The form names the DE, opens its
// seat (role, title, the supervisor seat: one of yours by default) and picks which of the role
// memory your agent holds is copied to the new DE. Personal memory is never offered. The copy goes
// through Recall's import with new provenance (who hired, from which crew, when), so it waits
// until Recall has the DE's memory ready; the DE's profile shows how it went. One POST
// (`crews/me/hire-as-de`); the form's lists are read when it is opened.
import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SettingsSection,
  Skeleton,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { UserPlusIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { OrgChart } from "../chart/chart-model";
import { chartKeys, orgChartQuery } from "../chart/chart-queries";
import type { CompanyAccess } from "../company";
import { useReachConfirm } from "../memory/reach-confirm";
import { crewMemoryGrantsQuery, keys, rolesQuery } from "../queries";
import { problemText } from "../settings/problem-text";

type CrewView = OperationOutput<"crews.me">;
type Grants = OperationOutput<"crews.memoryGrants">;
type Hired = OperationOutput<"crews.hireAsDe">;

const keyOf = (g: { bucket: string; subject_id: string }) => `${g.bucket}:${g.subject_id}`;

export function HireAsDeSection({
  api,
  company,
  view,
}: {
  api: Api;
  company: CompanyAccess;
  view: CrewView;
}) {
  const [open, setOpen] = useState(false);
  const [hired, setHired] = useState<Hired | null>(null);
  return (
    <SettingsSection
      id="hire-as-de"
      icon={UserPlusIcon}
      title="Hire as a DE"
      actions={
        open || hired ? null : (
          <Button variant="secondary" onClick={() => setOpen(true)}>
            Hire as a DE
          </Button>
        )
      }
    >
      {hired ? (
        <Notice tone="success" data-slot="hire-done">
          <span>
            {hired.copy
              ? `Hired. ${hired.copy.sources.length} memory ${hired.copy.sources.length === 1 ? "group is" : "groups are"} copied once Recall has the DE's memory ready. `
              : "Hired. "}
            <Link
              to="/c/$slug/agents/de/$id"
              params={{ slug: company.slug, id: hired.de_membership_id }}
              className="font-medium text-primary hover:underline"
            >
              Open the DE
            </Link>
          </span>
        </Notice>
      ) : open ? (
        <HireLoader
          api={api}
          company={company}
          view={view}
          onCancel={() => setOpen(false)}
          onHired={setHired}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          A new DE on its own seat, with the role memory you pick copied in; your agent and your
          private notes stay as they are.
        </p>
      )}
    </SettingsSection>
  );
}

/** A seat a person holds (the only kind the DE may report to), marked when it is the hirer's. */
export type SupervisorSeat = { id: string; title: string; you: boolean };

/**
 * The seats the DE may report to: open seats with a person on them, from the org chart (its
 * holders name who sits where; the seats list does not). DE seats and empty seats are left out,
 * so the picker never offers a supervisor the server would refuse.
 */
export function supervisorSeats(
  chart: { seats: OrgChart["seats"] },
  ownerMembershipId: string,
): SupervisorSeat[] {
  return chart.seats
    .filter((s) => s.status !== "closed" && s.holders.some((h) => h.membership_kind === "person"))
    .map((s) => ({
      id: s.id,
      title: s.title,
      you: s.holders.some((h) => h.membership_id === ownerMembershipId),
    }))
    .sort((a, b) => Number(b.you) - Number(a.you) || a.title.localeCompare(b.title));
}

function HireLoader(props: {
  api: Api;
  company: CompanyAccess;
  view: CrewView;
  onCancel: () => void;
  onHired: (h: Hired) => void;
}) {
  const { api, company, view } = props;
  const grants = useQuery(crewMemoryGrantsQuery(api, company.id));
  const roles = useInfiniteQuery(rolesQuery(api, company.id));
  const chart = useQuery(orgChartQuery(api, company.id));
  if (grants.isPending || roles.isPending || chart.isPending)
    return (
      <div role="status" aria-label="Loading the hire form" className="flex flex-col gap-2">
        <Skeleton className="h-10" />
        <Skeleton className="h-10" />
      </div>
    );
  const error = grants.error ?? roles.error ?? chart.error;
  if (error || !grants.data || !roles.data || !chart.data)
    return (
      <Notice tone="danger" role="alert">
        {problemText(error, "The hire form could not be loaded.")}
      </Notice>
    );
  return (
    <HireForm
      {...props}
      grants={grants.data}
      roles={roles.data.pages.flatMap((p) => p.data)}
      seats={supervisorSeats(chart.data, view.crew.owner_membership_id)}
    />
  );
}

function HireForm({
  api,
  company,
  view,
  grants,
  roles,
  seats,
  onCancel,
  onHired,
}: {
  api: Api;
  company: CompanyAccess;
  view: CrewView;
  grants: Grants;
  roles: { id: string; name: string }[];
  seats: SupervisorSeat[];
  onCancel: () => void;
  onHired: (h: Hired) => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const baseId = useId();
  // The memory on offer: what the crew holds now, labelled as My agent › Role memory labels it.
  const held = grants.data.flatMap((g) => {
    const a = grants.available.find((x) => keyOf(x) === keyOf(g));
    return a ? [a] : [];
  });
  const [name, setName] = useState(view.agent.display_name);
  const [title, setTitle] = useState("");
  const [roleId, setRoleId] = useState(roles[0]?.id ?? "");
  // One of the hirer's own seats by default (the picker sorts them first).
  const [supervisor, setSupervisor] = useState(seats[0]?.id ?? "");
  const [picked, setPicked] = useState<Set<string>>(new Set(held.map(keyOf)));
  // D8: the new DE is first shown what it will read (a new seat, in no team yet)
  const { confirmReach, reachDialog } = useReachConfirm(api, co);
  const hire = useMutation({
    mutationFn: async () => {
      if (
        !(await confirmReach({
          kind: "seat_holder",
          membership_id: null,
          add_seat_id: null,
          end_seat_id: null,
        }))
      )
        return null;
      return api.client.crews.hireAsDe({
        params: { co },
        body: {
          display_name: name.trim(),
          seat: { role_id: roleId, title: title.trim(), supervisor_seat_id: supervisor },
          memory_groups: held
            .filter((a) => picked.has(keyOf(a)))
            .map((a) => ({ bucket: a.bucket, subject_id: a.subject_id })),
        },
      });
    },
    onSuccess: (h) => {
      if (!h) return;
      void qc.invalidateQueries({ queryKey: keys.seats(co) });
      void qc.invalidateQueries({ queryKey: keys.digitalEmployees(co) });
      void qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) });
      onHired(h);
    },
  });
  const ready = name.trim() && title.trim() && roleId && supervisor;
  return (
    <form
      aria-label="Hire as a DE"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) hire.mutate();
      }}
    >
      {reachDialog}
      <Field>
        <FieldLabel htmlFor={`${baseId}-name`}>DE name</FieldLabel>
        <Input
          id={`${baseId}-name`}
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field>
          <FieldLabel htmlFor={`${baseId}-title`}>Seat title</FieldLabel>
          <Input
            id={`${baseId}-title`}
            value={title}
            maxLength={200}
            placeholder="e.g. Recruiting assistant (DE)"
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={`${baseId}-role`}>Role</FieldLabel>
          <NativeSelect
            id={`${baseId}-role`}
            className="w-full"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            {roles.map((r) => (
              <NativeSelectOption key={r.id} value={r.id}>
                {r.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      </div>
      <Field>
        <FieldLabel htmlFor={`${baseId}-supervisor`}>Reports to</FieldLabel>
        <NativeSelect
          id={`${baseId}-supervisor`}
          className="w-full"
          value={supervisor}
          onChange={(e) => setSupervisor(e.target.value)}
        >
          {seats.map((s) => (
            <NativeSelectOption key={s.id} value={s.id}>
              {s.title}
              {s.you ? " (you)" : ""}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <FieldDescription>
          {seats.length ? "Seats a person holds." : "No seat has a person on it yet."}
        </FieldDescription>
      </Field>
      <fieldset className="flex flex-col gap-2" data-slot="hire-memory">
        <legend className="mb-1 text-sm font-medium">Memory to copy</legend>
        {held.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Your agent holds no role memory, so the DE starts with its seat's memory only.
          </p>
        ) : (
          held.map((a) => {
            const id = `${baseId}-${a.bucket}-${a.subject_id}`;
            return (
              <div key={keyOf(a)} data-bucket={a.bucket} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  aria-label={a.label}
                  checked={picked.has(keyOf(a))}
                  onCheckedChange={(on) =>
                    setPicked((cur) => {
                      const next = new Set(cur);
                      if (on === true) next.add(keyOf(a));
                      else next.delete(keyOf(a));
                      return next;
                    })
                  }
                />
                <Label htmlFor={id} className="font-normal">
                  {a.label}
                </Label>
              </div>
            );
          })
        )}
      </fieldset>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!ready || hire.isPending}>
          {hire.isPending ? "Hiring…" : "Hire"}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {hire.error ? (
        <Notice tone="danger" role="alert">
          {problemText(hire.error, "The DE could not be hired.")}
        </Notice>
      ) : null}
    </form>
  );
}
